import { sortBy } from 'lodash-es';
import { makeAutoObservable, runInAction } from 'mobx';

import {
  CatalogRequests,
  type CategoryListAPI,
  LandingPage,
} from '~/api/Catalog';
import { company } from '~/company/Company';
import { CategoryDisplayType } from '~/components/CategoryBlocks/CategoryBlock';
import { mainStore } from '~/stores';
import { Category } from '~/stores/CategoriesStore/Category';
import { Task } from '~/stores/shared/Task';
import { catalogWarehouseParam } from '~/utils/catalogWarehouse';
import { copyWith } from '~/utils/copy';
import { PreloadCache } from '~/utils/preloadCache';

export interface ShopCategoryPlace {
  /** Top-level category the node belongs to. */
  zone: Category;
  /** The node of the shop tree, with the children and their product counts. */
  node: Category;
  /** The node's parent, absent for a zone. */
  parent?: Category;
}

export class ShopCategories {
  public readonly request = new Task(this.loadCategories.bind(this));

  private data: Category[] = [];

  constructor(serverWarehouse?: string, serverLang?: string) {
    if (serverWarehouse) {
      this.init(serverWarehouse, serverLang);
    }

    makeAutoObservable(this);
  }

  public static preloadedData = new PreloadCache<CategoryListAPI[]>({
    max: 500,
    ttlMs: 60_000,
  });

  public static async preload(warehouse: string, serverLang?: string) {
    const data = await this.request(warehouse, serverLang);

    this.preloadedData.set(this.getCategoriesId(warehouse, serverLang), data);

    return data;
  }

  public static removePreloadedData(id: string) {
    this.preloadedData.delete(id);
  }

  public static getCategoriesId(warehouse: string, serverLang?: string) {
    return `${warehouse}.${serverLang}`;
  }

  /**
   * Drops the SEO copy from the navigation tree.
   *
   * This tree exists to render the supermenu, the categories subheader, the
   * search dropdown and the categories carousel — none of which show a category
   * description. The category page renders one, but it loads its own category
   * through `CatalogCategory`. On CityDrinks these descriptions were 309KB of
   * the 778KB serialized into every HTML document, paid for by every visitor on
   * every cold load and parsed again during hydration.
   *
   * The field is nulled rather than deleted so the payload still satisfies
   * `CategoryAPI`. Applied to the shared request, so SSR and the client see the
   * same shape and hydration stays consistent. Companies whose catalog shows
   * the top-level category's note keep the top-level descriptions.
   */
  private static stripDescriptions(
    categories: CategoryListAPI[],
    keepTopLevel = false,
  ): CategoryListAPI[] {
    for (const node of categories) {
      if (!keepTopLevel) {
        node.category.description = null;
      }
      if (node.children) {
        ShopCategories.stripDescriptions(node.children);
      }
    }

    return categories;
  }

  private static request(warehouse: string, serverLang?: string) {
    return CatalogRequests.getCategories(
      {
        ...catalogWarehouseParam(warehouse),
        landingPage: [LandingPage.SHOP],
        depth: 3,
      },
      serverLang,
    ).then(({ data }) =>
      ShopCategories.stripDescriptions(data, company.catalogZoneNoteEnabled),
    );
  }

  public get list(): Readonly<Category[]> {
    return this.data;
  }

  /**
   * Finds a category of the tree by its numeric id, down to the third level,
   * with its zone, its children carrying product counts and its parent.
   */
  public locate(numberId: number): ShopCategoryPlace | null {
    for (const zone of this.data) {
      if (zone.numberId === numberId) {
        return { zone, node: zone };
      }
      for (const child of zone.subcategory) {
        if (child.numberId === numberId) {
          return { zone, node: child, parent: zone };
        }
        const grandchild = child.subcategory.find(
          (item) => item.numberId === numberId,
        );
        if (grandchild) {
          return { zone, node: grandchild, parent: child };
        }
      }
    }

    return null;
  }

  public get mainCategories() {
    return this.list.filter(
      (category) =>
        category.subcategory &&
        category.subcategory.length > 0 &&
        !category.showInSupermenu,
    );
  }

  get startShoppingCategory() {
    const category = this.data.find(({ name }) =>
      name.toLowerCase().includes('start shopping'),
    );

    if (!category) {
      return null;
    }

    return copyWith(category, {
      externalLink: '/shop',
      displayType: CategoryDisplayType.CATEGORIES_GRID_ONE_UNMASKED,
    });
  }

  private async loadCategories(warehouse: string) {
    try {
      const data = await ShopCategories.request(warehouse);

      runInAction(() => {
        this.data = sortBy(data, 'sorting')
          .filter(Category.validate)
          .map((data) => new Category(data));
      });

      return data;
    } catch (error) {
      return mainStore.errorHandler(error as never, 'requestCategories');
    }
  }

  private init(serverWarehouse: string, serverLang?: string) {
    const isSSR = typeof window === 'undefined';
    const ssrDegraded = !isSSR && window.__INITIAL_STATE__?.ssrDegraded;
    const preloadedData = ssrDegraded
      ? null
      : (!isSSR ? window.__INITIAL_STATE__?.shopCategories : null) ||
        ShopCategories.preloadedData.get(
          ShopCategories.getCategoriesId(serverWarehouse, serverLang),
        );

    if (!preloadedData || preloadedData.length === 0) {
      // Degraded SSR or CSR without data — fetch fresh categories on mount.
      if (!isSSR) {
        this.request.run(serverWarehouse);
      }
      return;
    }

    this.data = sortBy(preloadedData, 'sorting')
      .filter(Category.validate)
      .map((data) => new Category(data));
  }
}
